<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import type {
  CatalogueItemDetail,
  CatalogueItemGroup,
  ChildItemWithQuantity,
  ChildItemData,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import { useCataloguePermissions } from '@/composables/catalogue/useCataloguePermissions';
import AddChildItemDialog from '@/components/catalogue/AddChildItemDialog.vue';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';

const { $gettext } = gettext;
const { canEditItem } = useCataloguePermissions();
const actions = inject(CatalogueItemActionsKey)!;

const props = defineProps<{
  item: CatalogueItemDetail;
  groups: CatalogueItemGroup[];
}>();

const canEdit = computed(() => {
  return canEditItem(props.item);
});

const showAddChildDialog = ref(false);
const showRemoveChildDialog = ref(false);
const childToRemove = ref<ChildItemWithQuantity | null>(null);
const editingQuantity = ref<string | null>(null);
const editingQuantityValue = ref<number>(0);

const handleAddChild = (childData: ChildItemData) => {
  actions.addChild(childData);
  showAddChildDialog.value = false;
};

const handleRemoveChild = (child: ChildItemWithQuantity) => {
  childToRemove.value = child;
  showRemoveChildDialog.value = true;
};

const confirmRemoveChild = () => {
  if (childToRemove.value) {
    actions.removeChild(childToRemove.value.uuid);
    showRemoveChildDialog.value = false;
    childToRemove.value = null;
  }
};

const cancelRemoveChild = () => {
  showRemoveChildDialog.value = false;
  childToRemove.value = null;
};

const startEditQuantity = (child: ChildItemWithQuantity) => {
  editingQuantity.value = child.uuid;
  editingQuantityValue.value = child.quantity;
};

const saveQuantity = (childUuid: string) => {
  if (editingQuantityValue.value > 0) {
    actions.updateChildQuantity(childUuid, editingQuantityValue.value);
  }
  editingQuantity.value = null;
  editingQuantityValue.value = 0;
};

const cancelEditQuantity = () => {
  editingQuantity.value = null;
  editingQuantityValue.value = 0;
};
</script>

<template>
  <div>
    <div class="d-flex align-center mb-4">
      <h3 class="text-h6">{{ $gettext('Components') }}</h3>
      <v-spacer />
      <r-btn
        v-if="canEdit"
        icon="mdi-plus"
        variant="outlined"
        @click="showAddChildDialog = true"
        >{{ $gettext('Add Component') }}</r-btn
      >
    </div>

    <v-table v-if="item.children && item.children.length > 0" class="mb-4">
      <thead>
        <tr>
          <th class="text-left">{{ $gettext('Component') }}</th>
          <th class="text-left">{{ $gettext('Quantity') }}</th>
          <th v-if="canEdit" class="text-right">{{ $gettext('Actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="child in item.children" :key="child.uuid">
          <td>
            <span class="cursor-pointer" @click="actions.openChild(child.uuid)">
              {{ child.name }}
            </span>
          </td>
          <td>
            <div
              v-if="editingQuantity === child.uuid"
              class="d-flex align-center"
            >
              <v-text-field
                v-model.number="editingQuantityValue"
                type="number"
                min="1"
                density="compact"
                hide-details
                class="mr-2"
                style="max-width: 80px"
                @keyup.enter="saveQuantity(child.uuid)"
                @keyup.esc="cancelEditQuantity"
              />
              <v-btn
                icon="mdi-check"
                size="small"
                color="success"
                variant="text"
                @click="saveQuantity(child.uuid)"
              />
              <v-btn
                icon="mdi-close"
                size="small"
                color="error"
                variant="text"
                @click="cancelEditQuantity"
              />
            </div>
            <div v-else class="d-flex align-center">
              <span class="mr-2">{{ child.quantity }}</span>
              <v-btn
                v-if="canEdit"
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="startEditQuantity(child)"
              />
            </div>
          </td>
          <td v-if="canEdit" class="text-right">
            <v-icon
              icon="mdi-delete"
              color="error"
              size="small"
              class="cursor-pointer"
              @click="handleRemoveChild(child)"
            />
          </td>
        </tr>
      </tbody>
    </v-table>
    <v-alert
      v-else
      type="info"
      variant="tonal"
      :text="$gettext('This item has no components.')"
    />

    <AddChildItemDialog
      v-model="showAddChildDialog"
      :parent-uuid="item.uuid"
      :groups="groups"
      :existing-child-uuids="item.children?.map((c) => c.uuid) || []"
      @save="handleAddChild"
    />

    <!-- Remove Child Confirmation Dialog -->
    <v-dialog v-model="showRemoveChildDialog" max-width="500px">
      <div class="pa-6 bg-white" style="border-radius: 8px">
        <div class="d-flex align-center mb-4">
          <h2 class="text-h5">{{ $gettext('Remove Component') }}</h2>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" @click="cancelRemoveChild" />
        </div>

        <div>
          <p>
            {{ $gettext('Are you sure you want to remove this component?') }}
          </p>
          <p v-if="childToRemove" class="font-weight-bold">
            {{ childToRemove.name }}
          </p>
        </div>

        <div class="d-flex justify-end mt-6">
          <v-btn
            color="grey-darken-1"
            variant="text"
            class="mr-2"
            @click="cancelRemoveChild"
          >
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-btn color="error" variant="text" @click="confirmRemoveChild">
            {{ $gettext('Remove') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>
